import React, { useState, useEffect } from "react";
import useHttp from "../../Hooks/use-http";
import {
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  Snackbar,
  CircularProgress,
  Alert,
} from "@mui/material";
import { FileUpload as FileUploadIcon } from "@mui/icons-material";
import { useLocation } from "react-router-dom";

const DocumentUpload = () => {
  const [documents, setDocuments] = useState([]);
  const [isUpload, setIsUpload] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const { isLoading, sendRequest } = useHttp();
  const [getCorporatesFromCorporates, setGetCorporatesFromCorporates] =
    useState(null);
  const [isUploading, setIsUploading] = useState(false); // To show loader during file upload
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [validationErrors, setValidationErrors] = useState({}); // To store validation errors

  const location = useLocation();
  useEffect(() => {
    if (location.state && location.state.data) {
      setGetCorporatesFromCorporates(location.state.data);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchDocumentsMaster = async () => {
      try {
        const data = await sendRequest({
          url: "/documentsmaster",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: {
            emailID: localStorage.getItem("user"),
          },
        });

        if (data?.status === 200 || data?.status === 201) {
          const filteredDocuments =
            data?.documentMaster.filter(
              (doc) => doc.category === "Corporate"
            ) || [];
          const newDocuments = filteredDocuments.map((doc) => ({
            name: doc.documentName || "",
            type: doc.type,
            documentID: doc.documentID,
            number: "",
            category: doc.category,
            expiryDate: "",
            file: null,
            url: "",
            error: "",
            preview: "",
            isUploaded: false, // New property for upload status
            required: doc.required,
          }));
          setDocuments(newDocuments);

          // Fetch existing documents for these document IDs
          const documentIDs = filteredDocuments.map((doc) => doc.documentID);
          if (documentIDs.length > 0) {
            fetchExistingDocuments(documentIDs);
          }
        }
      } catch (error) {
        console.error("Error fetching document master", error);
      }
    };

    const fetchExistingDocuments = async (documentIDs) => {
      try {
        const response = await sendRequest({
          url: "/documents",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: {
            emailID: localStorage.getItem("user"),
            documentIds: documentIDs,
            corporateID: getCorporatesFromCorporates?.corporateID,
          },
        });

        if (response?.status === 200) {
          setExistingDocuments(response.documents || []);
        }
      } catch (error) {
        console.error("Error fetching existing documents", error);
      }
    };

    fetchDocumentsMaster();
  }, [sendRequest, getCorporatesFromCorporates?.corporateID]);

  const documentChangeHandler = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const preview = file.type.startsWith("image/")
        ? URL.createObjectURL(file) // Show image preview
        : file.name; // Show filename if not an image
      setDocuments((prev) => {
        const updatedDocuments = [...prev];
        updatedDocuments[index] = {
          ...updatedDocuments[index],
          file: file,
          preview: preview,
          error: "",
        };
        return updatedDocuments;
      });
    }
  };

  const validateDocument = (doc) => {
    let error = {};
    if (doc.required && !doc.number)
      error.number = "Document number is required.";
    if (doc.required && !doc.expiryDate)
      error.expiryDate = "Expiry date is required.";
    if (!doc.file) error.file = "File is required.";
    return error;
  };

  const uploadFileToBackend = async (index) => {
    setIsUploading(true);

    const file = documents[index].file;
    const FolderName = "corporate";
    const uniqueID = getCorporatesFromCorporates?.corporateID;
    const documentID = documents[index].documentID;

    try {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("fileName", file?.name ?? "random");
      formData.append("folderName", FolderName);
      formData.append("uniqueID", uniqueID);
      formData.append("documentID", documentID);
      formData.append("fileExtension", fileExtension);

      // const response = await fetch("http://localhost:4001/documentfileupload", {
      const response = await fetch(
        "http://52.183.138.214:4001/documentfileupload",
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();

      if (response.ok) {
        const fileUrl = data?.data;
        setDocuments((prev) => {
          const updatedDocuments = [...prev];
          updatedDocuments[index] = {
            ...updatedDocuments[index],
            url: fileUrl,
            isUploaded: true, // Mark as uploaded
          };
          return updatedDocuments;
        });
        setSnackbarMessage("File uploaded successfully!");
      } else {
        console.error("Error in upload:", data.message);
        setSnackbarMessage("Error uploading file: " + data.message);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      setSnackbarMessage("Error uploading file!");
    } finally {
      setIsUploading(false);
      setSnackbarOpen(true);
    }
  };

  const submitHandler = async () => {
    let errors = {};
    documents.forEach((doc, index) => {
      const documentError = validateDocument(doc);
      if (Object.keys(documentError).length > 0) {
        errors[index] = documentError;
      }
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return; // Stop submission if validation errors exist
    }

    setIsUpload(true);
    const payload = documents.map((doc) => ({
      documentNumber: doc.number,
      expiryDate: doc.expiryDate,
      documentURL: doc.url,
      category: doc.category,
      createdOn: new Date(),

      documentID: doc.documentID,
      ...(doc.category === "Corporate" && {
        corporateID: getCorporatesFromCorporates.corporateID,
      }),
      isVerified: doc.required ? 0 : null,
    }));

    try {
      const response = await sendRequest({
        url: "/addupdatedocument",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: { emailID: localStorage.getItem("user"), data: payload },
      });
      setSnackbarMessage(
        response.status === 200 || response.status === 201
          ? "Documents uploaded successfully!"
          : "Error uploading documents!"
      );
    } catch (error) {
      setSnackbarMessage("Error uploading documents!");
    } finally {
      setSnackbarOpen(true);
      setIsUpload(false);
    }
  };
  return (
    <Box
      style={{ marginTop: "3%" }}
      sx={{
        maxWidth: "90%",
        margin: "0 auto",
        padding: 4,
        backgroundColor: "white",
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: "#3a3a3a",
          fontWeight: "bold",
          fontSize: "18px",
          marginBottom: 2,
        }}
      >
        Document Upload
      </Typography>

      {isLoading && (
        <CircularProgress sx={{ display: "block", margin: "0 auto" }} />
      )}

      <Grid container spacing={2} sx={{ marginTop: 2 }}>
        {documents.map((doc, index) => {
          // Find existing data for this documentID
          const existingDataForDoc = existingDocuments.filter(
            (existing) => existing.documentID === doc.documentID
          );

          return (
            <Grid item xs={12} key={index}>
              <Box
                sx={{
                  padding: 2,
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  backgroundColor: "#fafafa",
                  boxShadow: 2,
                  transition: "all 0.3s ease",
                  "&:hover": { boxShadow: 4 },
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        color: "black",
                        fontWeight: "500",
                        fontSize: "18px",
                      }}
                    >
                      <strong>
                        {doc.name}
                        {doc.required && (
                          <span style={{ color: "red" }}>*</span>
                        )}
                      </strong>
                    </Typography>

                    {/* Display validation errors for the document */}
                    {validationErrors[index] && (
                      <Box sx={{ color: "red", marginTop: 1 }}>
                        {validationErrors[index].number && (
                          <Typography variant="body2">
                            {validationErrors[index].number}
                          </Typography>
                        )}
                        {validationErrors[index].expiryDate && (
                          <Typography variant="body2">
                            {validationErrors[index].expiryDate}
                          </Typography>
                        )}
                        {validationErrors[index].file && (
                          <Typography variant="body2">
                            {validationErrors[index].file}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Grid>

                  {/* Show existing data as a preview */}
                  {existingDataForDoc.length > 0 && (
                    <Grid item xs={12}>
                      <Typography sx={{ color: "#007bff", fontSize: "16px" }}>
                        Existing Document Details:
                      </Typography>
                      {existingDataForDoc.map((existingData, idx) => (
                        <Grid container spacing={2} key={idx}>
                          <Grid item xs={4}>
                            <Typography
                              variant="body2"
                              sx={{ color: "#6c757d" }}
                            >
                              <strong>Document Number:</strong>{" "}
                              {existingData.documentNumber || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography
                              variant="body2"
                              sx={{ color: "#6c757d" }}
                            >
                              <strong>Expiry Date:</strong>{" "}
                              {existingData.expiryDate.split("T")[0] || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography
                              variant="body2"
                              sx={{ color: "#6c757d" }}
                            >
                              <strong>Uploaded File:</strong>
                              {existingData.documentURL ? (
                                <a
                                  href={existingData.documentURL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "#007bff",
                                    textDecoration: "none",
                                    fontWeight: "bold",
                                  }}
                                >
                                  View File
                                </a>
                              ) : (
                                "No file uploaded"
                              )}
                            </Typography>
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  {/* Inputs for updates */}
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Document Number"
                      value={doc.number || ""}
                      onChange={(e) => {
                        const updatedDocuments = [...documents];
                        updatedDocuments[index].number = e.target.value;
                        setDocuments(updatedDocuments);
                      }}
                      variant="standard"
                      size="small"
                      sx={{
                        backgroundColor: "#fafafa",
                        "&:hover": { backgroundColor: "#f1f1f1" },
                      }}
                      error={!!validationErrors[index]?.number}
                      helperText={validationErrors[index]?.number}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Expiry Date"
                      type="date"
                      value={doc.expiryDate || ""}
                      onChange={(e) => {
                        const updatedDocuments = [...documents];
                        updatedDocuments[index].expiryDate = e.target.value;
                        setDocuments(updatedDocuments);
                      }}
                      variant="standard"
                      size="small"
                      sx={{
                        backgroundColor: "#fafafa",
                        "&:hover": { backgroundColor: "#f1f1f1" },
                      }}
                      InputLabelProps={{ shrink: true }}
                      error={!!validationErrors[index]?.expiryDate}
                      helperText={validationErrors[index]?.expiryDate}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    {!doc.isUploaded ? (
                      <>
                        <Button
                          variant="contained"
                          component="label"
                          sx={{
                            width: "70%",
                            padding: "8px 0",
                            marginLeft: "5%",
                            backgroundColor: "#1976d2",
                            color: "#fff",
                            boxShadow: 2,
                            transition: "all 0.3s ease",
                            "&:hover": { backgroundColor: "#1565c0" },
                          }}
                          startIcon={<FileUploadIcon />}
                        >
                          {doc.preview ? "File Selected" : "Select File"}
                          <input
                            type="file"
                            hidden
                            onChange={(e) => documentChangeHandler(index, e)}
                          />
                        </Button>

                        {/* Preview the file */}
                        {doc.preview && (
                          <Box
                            sx={{
                              marginTop: 1,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              backgroundColor: "#f4f4f4",
                              padding: "10px",
                              borderRadius: 1,
                              boxShadow: 1,
                              transition: "all 0.3s ease",
                            }}
                          >
                            {doc.preview.startsWith("image/") ? (
                              <img
                                src={doc.preview}
                                alt="Preview"
                                style={{
                                  width: "100px",
                                  height: "100px",
                                  objectFit: "cover",
                                  borderRadius: 5,
                                }}
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ color: "#007bff" }}
                              >
                                {doc.preview}
                              </Typography>
                            )}
                            <Button
                              variant="contained"
                              onClick={() => uploadFileToBackend(index)}
                              sx={{
                                width: "70%",
                                marginTop: 1,
                                padding: "8px 0",
                                backgroundColor: "#44B150",
                                color: "#fff",
                                boxShadow: 2,
                              }}
                              disabled={isUploading}
                            >
                              {isUploading ? (
                                <CircularProgress size={24} />
                              ) : (
                                "Upload File"
                              )}
                            </Button>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Typography
                        sx={{
                          color: "green",
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      >
                        Successfully Uploaded
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 3,
        }}
      >
        <Button
          variant="contained"
          onClick={submitHandler}
          sx={{
            padding: "10px 20px",
            backgroundColor: "#44B150",
            boxShadow: 2,
          }}
          disabled={isUpload}
        >
          Upload All Documents
        </Button>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarMessage.includes("Error") ? "error" : "success"}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentUpload;
